/**
 * Org health messages: what the nodes and the cloud report about the health of the system,
 * delivered over the notifications stream to administrators who keep the type. The type and
 * the object together name a problem: a warning or an error opens it, an info ends it.
 */

import { z } from 'zod';

import { declareMediaServerDefault } from '@/api/mediaServerFill';

import { HealthMessage as GeneratedHealthMessageSchema } from './generated/vms_database.zod';

/**
 * The message types the UI knows. A frozen object plus its union rather than an
 * enum, because the type arrives as JSON on the notifications stream.
 */
export const HealthMessageType = {
    CameraReplacementWarning: 'cameraReplacementWarning',
    StorageFull: 'storageFull',
} as const;

export type HealthMessageType = (typeof HealthMessageType)[keyof typeof HealthMessageType];

/** Hardware named in a health message, without credentials. */
export const HealthMessageHardwareSchema = z.object({
    model: z.string(),
    url: z.string(),
    mac: z.string(),
});
export type HealthMessageHardware = z.infer<typeof HealthMessageHardwareSchema>;

/** The details of a `cameraReplacementWarning`: the camera and both hardware. */
export interface CameraReplacementDetails {
    deviceId: string;
    deviceName: string;
    current: HealthMessageHardware;
    original: HealthMessageHardware;
    /** When the original hardware was last seen. */
    detectedAtMs: number;
}

/** The original hardware of a replaced camera is on the network again. */
export interface CameraReplacementWarning extends HealthMessagePayload {
    type: typeof HealthMessageType.CameraReplacementWarning;
    details: CameraReplacementDetails;
}

/**
 * How bad the problem a health message reports is. A frozen object plus its union rather
 * than an enum, because the severity arrives as JSON.
 */
export const HealthSeverity = {
    Info: 'info',
    Warning: 'warning',
    Error: 'error',
} as const;

export type HealthSeverity = (typeof HealthSeverity)[keyof typeof HealthSeverity];

/**
 * The fields every health message carries, whatever its type: a node writes each of them, the
 * cloud leaves out the ones that are empty in its own records and in the log it answers.
 */
export const HealthMessagePayloadSchema = GeneratedHealthMessageSchema.extend({
    /** A node that sent none meant a warning, as its rules do. */
    severity: declareMediaServerDefault(z.string(), HealthSeverity.Warning),
});
export type HealthMessagePayload = z.infer<typeof HealthMessagePayloadSchema>;

/** Which health bucket a `health` frame of the notifications stream is about. */
export const HealthFeed = {
    /** The current issues; the log is read by request. */
    Issues: 'issues',
} as const;

export type HealthFeed = (typeof HealthFeed)[keyof typeof HealthFeed];

/** What a `health` frame says happened to its key. */
export const HealthFrameAction = {
    /** The record was written; `message` carries it. */
    Put: 'put',
    /** The key is gone: an issue over, or a deleted server's purge. */
    Delete: 'delete',
} as const;

export type HealthFrameAction = (typeof HealthFrameAction)[keyof typeof HealthFrameAction];

/**
 * A `health` frame of the notifications stream: one current issue written or removed. The
 * stream replays the issues when it opens and pushes each change as it happens.
 */
export const HealthFrameSchema = z.object({
    type: z.literal('health'),
    feed: z.enum([HealthFeed.Issues]),
    action: z.enum([HealthFrameAction.Put, HealthFrameAction.Delete]),
    key: z.string(),
    /** The record as its source wrote it; absent on a delete. */
    message: HealthMessagePayloadSchema.optional(),
});
export type HealthFrame = z.infer<typeof HealthFrameSchema>;

/**
 * One record of the organization's health log or one of its current issues, as the stream
 * delivered it.
 */
export interface FeedHealthMessage {
    /**
     * The record's key in its bucket: `{serverId}.{id}` on the log, what Remove names, and
     * `{serverId}.{type}[.{objectId}]` among the issues.
     */
    key: string;
    /** The record as its source wrote it. */
    message: HealthMessagePayload;
}

/**
 * What narrows a page of the health log, as `GET /vms/api/v1/health/log` takes it: the
 * records before the cursor, of a severity, of a server, in a time window.
 */
export interface HealthLogFilter {
    /** The page older than this seq. */
    beforeSeq?: number;
    /** The page newer than this seq: the records right after it. */
    afterSeq?: number;
    limit?: number;
    fromMs?: number;
    toMs?: number;
    severity?: string;
    serverId?: string;
    /** The types of problem, as `cameraOffline`, any of them. */
    types?: string[];
}

/** Where a page of the log is cut: older than a seq, newer than one, or the newest page. */
export type HealthLogCursor =
    { beforeSeq: number } | { afterSeq: number } | Record<string, never>;

/** What narrows the log, apart from where the page is cut. */
export type HealthLogNarrowing = Pick<
    HealthLogFilter, 'fromMs' | 'toMs' | 'severity' | 'serverId' | 'types'
>;

/** One page asked of the log: how it is narrowed and where it is cut. */
export interface HealthLogRequest extends HealthLogNarrowing {
    cursor: HealthLogCursor;
}

/** A page of the health log as the store holds it, with whether pages lie past its edges. */
export interface HealthLogPage {
    records: FeedHealthMessage[];
    hasOlder: boolean;
    hasNewer: boolean;
    isLoading: boolean;
    error: Error | null;
}
